<script lang="ts" setup>
  import { useRoute, useRouter } from 'vue-router'
  import { BreadcrumbList } from '@/helpers/router.ts'
  import Breadcrumb from 'primevue/breadcrumb'
  import { markRaw, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import type { MenuItem } from 'primevue/menuitem'
  import { HomeIcon } from '@/config/icons.ts'
  import ButtonDefault from '@/elements/ButtonDefault.vue'
  import { useDashboard } from '@/composables/useDashboard.ts'
  import { useFeatureFlags } from '@/composables/useFeatureFlag.ts'

  const route = useRoute()
  const router = useRouter()
  const dashboard = useDashboard()
  const isEditing = dashboard.isEditing
  const isSaveAllowed = dashboard.isSaveAllowed
  const isSaving = dashboard.isSaving
  const featureFlag = useFeatureFlags()
  const { t } = useI18n()

  const home = ref<MenuItem>({
    iconComp: markRaw(HomeIcon),
    path: '/',
    name: 'home'
  })

  const isDashboardPage = computed(() => {
    return route.name === 'dashboard-new' || (route.name === 'home' && featureFlag.isEnabled('dashboard_new'))
  })

  const onEditDashboardClick = () => {
    isEditing.value = true
    useEventBus('dashboard-edit-start').emit()
  }

  const onCancelEditClick = () => {
    useEventBus('dashboard-edit-cancel').emit()
  }

  const onSaveEditClick = () => {
    useEventBus('dashboard-edit-save').emit()
  }

  const onResetClick = () => {
    useEventBus('dashboard-reset-click').emit()
  }

  const goTo = (item: MenuItem) => {
    if (item.name) {
      router.push({ name: item.name })
      return
    }

    if (item.url) {
      router.push(item.url)
      return
    }
  }

  const isCurrent = (item: MenuItem) => {
    if (item.url && item.url === route.path) {
      return true
    }

    if (item.name && item.name === route.name) {
      return true
    }

    return false
  }

  const onBreadcrumbClick = (event, item: MenuItem) => {
    if (isCurrent(item)) {
      event.preventDefault()
      useEventBus('same_route_click').emit()
      return
    } else
      goTo(item)
  }
</script>

<template>
  <div class="flex w-full items-center justify-center">
    <Breadcrumb
      :home="home"
      :model="BreadcrumbList"
      class="text-sm w-full items-center">
      <template #item="{ item }">
        <a
          v-if="(item.url || item.name)"
          class="cursor-pointer inline-flex gap-x-1 items-center hover:underline"
          @click="onBreadcrumbClick($event, item)">
          <component
            :is="item.iconComp"
            v-if="item.iconComp"
            class="size-4" />
          <span v-if="item.label">{{ item.label }}</span>
        </a>


        <span
          v-else
          class="inline-flex gap-x-1 items-center">
          <component
            :is="item.iconComp"
            v-if="item.iconComp"
            class="size-4" />
          <span v-if="item.label">{{ item.label }}</span>
        </span>

      </template>
    </Breadcrumb>
    <div class="mr-5" v-if="isDashboardPage && !isEditing">
      <ButtonDefault
        icon="EditIcon"
        size="small"
        outlined
        severity="secondary"
        variant="text"
        @click="onEditDashboardClick"
        v-tooltip.top="t('edit')" />
    </div>
    <div class="mr-5" v-else-if="isDashboardPage && isEditing">
      <ButtonGroup>
        <ButtonDefault
          icon="ResetIcon"
          size="small"
          outlined
          severity="secondary"
          variant="text"
          :disabled="isSaving"
          @click="onResetClick"
          v-tooltip.top="t('reset')" />
        <ButtonDefault
          icon="CancelIcon"
          size="small"
          outlined
          severity="secondary"
          variant="text"
          :disabled="isSaving"
          @click="onCancelEditClick"
          v-tooltip.top="t('cancel')" />
        <ButtonDefault
          icon="SaveIcon"
          size="small"
          outlined
          severity="secondary"
          variant="text"
          :loading="isSaving"
          :disabled="!isSaveAllowed || isSaving"
          @click="onSaveEditClick"
          v-tooltip.top="t('save')" />
      </ButtonGroup>
    </div>
  </div>
</template>

<style scoped>
  :deep(ol.p-breadcrumb-list li) {
    display: flex;
    align-items: center;
  }
</style>

<i18n>
{
  "de": {
    "edit": "Dashboard bearbeiten",
    "reset": "Auf Standard zurücksetzen",
    "cancel": "Bearbeitung abbrechen",
    "save": "Dashboard speichern"
  },
  "en": {
    "edit": "Edit dashboard",
    "reset": "Restore defaults",
    "cancel": "Cancel editing",
    "save": "Save dashboard"
  }
}
</i18n>
